import { RETURN_PARAM } from 'utils/return-url';

// The shared Sweat440 accounts app owns the login screen an expired session is handed
// back to, so signing in again happens off this origin entirely.
//
// `REACT_APP_ACCOUNTS_URL` overrides the target per environment. Without one, the dev
// server assumes the accounts app is running locally on its default port and every
// other environment targets the deployed app.
const DEPLOYED_ACCOUNTS_URL = 'https://accounts.sweat440.dev';
const LOCAL_ACCOUNTS_URL = 'http://localhost:5173';

// Only an absolute http(s) URL can be handed to `window.location`. A relative value
// is resolved against *this* app, so instead of leaving the origin the browser just
// rewrites the current pathname — and because this app uses hash history, that bad
// pathname then survives every later in-app navigation (`/undefined#/app/...` is the
// signature of an unset env var interpolated into a redirect target). Reject it here
// so a misconfiguration fails loudly instead of stranding the user on a broken URL.
const ABSOLUTE_HTTP_URL = /^https?:\/\//i;

export const accountsBaseUrl = () => {
  const configured =
    (process.env.REACT_APP_ACCOUNTS_URL || '').trim() ||
    (process.env.NODE_ENV === 'development'
      ? LOCAL_ACCOUNTS_URL
      : DEPLOYED_ACCOUNTS_URL);

  if (!ABSOLUTE_HTTP_URL.test(configured)) {
    throw new Error(
      `REACT_APP_ACCOUNTS_URL must be an absolute http(s) URL, got "${configured}" — a relative value would rewrite this app's pathname instead of redirecting to the accounts app.`
    );
  }

  // A trailing slash would double up against the query string appended below.
  return configured.replace(/\/+$/, '');
};

// Full URL the user is looking at right now, hash fragment included.
//
// Read this *before* clearing the auth state: doing so unmounts the current screen and
// PrivateRoute rewrites the hash to the in-app login route, so anything captured
// afterwards is the login URL rather than the page being left.
export const currentUrl = () => window.location.href;

// Login URL on the accounts app, carrying the destination to come back to.
export const buildAccountsLoginUrl = (returnUrl) => {
  const base = accountsBaseUrl();
  if (!returnUrl) return base;

  const separator = base.includes('?') ? '&' : '?';
  const answer = `${base}${separator}${RETURN_PARAM}=${encodeURIComponent(
    returnUrl
  )}`;

  return answer;
};

// One hand-off per tab, tracked so we can tell "sign-in needed" apart from "we already
// tried that and came back no better off".
//
// The two apps decide the same question from different places: the accounts app reads a
// persisted flag in its own localStorage, while this app asks the API. When they
// disagree — a stale accounts flag, or an API session this origin can't present — each
// one defers to the other and the browser bounces between them forever. Noticing the
// second attempt is what turns that loop into a single failed hand-off.
//
// sessionStorage rather than a module variable: a hand-off is a full page load, so
// in-memory state is gone by the time we come back. It is scoped to this tab and this
// origin, and survives leaving for another origin and returning. If storage is
// unavailable (private-mode Safari, cookies off) the guard just doesn't engage.
const HANDOFF_KEY = 'sweat440.accountsHandoff';

export const hasHandedOffToAccounts = () => {
  try {
    return window.sessionStorage.getItem(HANDOFF_KEY) === '1';
  } catch (error) {
    return false;
  }
};

const rememberAccountsHandoff = () => {
  try {
    window.sessionStorage.setItem(HANDOFF_KEY, '1');
  } catch (error) {
    // Nothing to do: the guard is best-effort.
  }
};

// Call once a session is confirmed, so a later expiry in the same tab is free to hand
// off again rather than going straight to the fallback.
export const forgetAccountsHandoff = () => {
  try {
    window.sessionStorage.removeItem(HANDOFF_KEY);
  } catch (error) {
    // Nothing to do: the guard is best-effort.
  }
};

// `replace` rather than `assign`: the expired page is a dead end, so Back should skip
// past it instead of landing on a route that bounces straight to login again.
export const redirectToAccountsLogin = (returnUrl) => {
  // Build first: a misconfigured URL throws, and an attempt that never left shouldn't
  // count against the one hand-off this tab gets.
  const url = buildAccountsLoginUrl(returnUrl);
  rememberAccountsHandoff();
  window.location.replace(url);
};
